import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { ArrowRightOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../../utils/persistentNavigation';
import { SEO_DEFAULTS } from '../../../utils/seoConfig';
import { generateFAQSchema, generateBreadcrumbSchema, JsonLd } from '../../../utils/structuredData';
import { trackEvent } from '../../../utils/analytics';
import { useInMarketingShell } from '../../Marketing/MarketingContext';
import useReveal from '../useReveal';
import FurtherReading from '../FurtherReading';
import { DEFAULT_ROLE_READING } from '../blogPosts';
import RoleContactModal from '../roles/RoleContactModal';
import {
  ROLE_CONFIGS,
  SHARED_TRAITS,
  SHARED_TEAMS_FAQ,
} from '../roles/roleConfigs';
import { VERTICAL_CONFIGS } from './verticalConfigs';
import StoreEventFrame from './StoreEventFrame';
import '../AIEmployeesLandingPage.css';
import '../roles/RoleLandingPage.css';
import './VerticalLandingPage.css';

// Optional per-vertical hero visual (config.heroVisual); pages without one render exactly as before.
const HERO_VISUALS = { 'shopify-store': StoreEventFrame };

/**
 * VerticalLandingPage — ONE shared template for the GTM beachhead industry
 * pages at /ai-employees/for-{industry} (Aug 2026: home services + real
 * estate teams). Driven entirely by verticalConfigs.js; renders on the
 * .aie-landing design system and reuses the rolp-* classes from the role
 * pages so the visual language matches exactly.
 *
 * DELIBERATE DIFFERENCES from RoleLandingPage (do not "fix" these):
 *  - NO math beat, NO salary anchor, NO vs-human comparison table: the
 *    Aug 11 2026 ruling freezes human-hire comparison framing on new copy.
 *    The narrative here is access ("the AI workforce reaching your
 *    industry"), never replacement.
 *  - NO count-up animations, so there is no prerender freeze logic needed.
 *  - Adds industry-specific sections: problem, named starter roles, an
 *    honesty block (the trust beat: what this cannot do yet), and a
 *    3-step how-hiring-works with a per-industry example prompt.
 *
 * Routing: flat single-segment URLs, so App.js declares one explicit route
 * per vertical, each passing verticalSlug as a prop.
 */

const Section = ({ className = '', label, title, sub, children, narrow }) => {
  const [ref, visible] = useReveal();
  return (
    <section className={`aie-section rolp-section ${narrow ? 'rolp-section--narrow' : ''} ${className}`} ref={ref}>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        {(label || title) && (
          <div className="aie-section-head rolp-section-head">
            {label && <span className="rolp-eyebrow">{label}</span>}
            {title && <h2 className="aie-section-title aie-display">{title}</h2>}
            {sub && <p className="aie-section-sub">{sub}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

const VerticalLandingPage = ({ verticalSlug }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();
  const [modalOpen, setModalOpen] = useState(false);

  const config = VERTICAL_CONFIGS[verticalSlug];
  if (!config) return <Navigate to="/ai-employees" replace />;

  const dark = inMarketingShell ? true : isDarkMode;
  const canonicalUrl = `${SEO_DEFAULTS.url}${config.path}`;
  const ogImage = `${SEO_DEFAULTS.url}/assets/ai-employees/${config.slug}-og.jpg`;

  const handleHire = (loc) => {
    trackEvent('cta_click', { cta_name: 'hire_ai_employee', role: config.contactRoleDisplay, location: loc });
    if (!currentUser) {
      saveIntendedUrl('/ai_employee');
      navigate('/signup');
      return;
    }
    navigate('/ai_employee');
  };

  const handleTalk = (loc) => {
    trackEvent('contact_cta_click', { role: config.contactRoleDisplay, cta_location: loc });
    setModalOpen(true);
  };

  const faq = [...config.faq, SHARED_TEAMS_FAQ];
  const cross = VERTICAL_CONFIGS[config.crossVertical];
  const HeroVisual = HERO_VISUALS[config.heroVisual];

  return (
    <>
      <Helmet>
        <title>{config.seo.title}</title>
        <meta name="description" content={config.seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        {config.mdAlternate && <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={config.seo.title} />
        <meta property="og:description" content={config.seo.description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content={`AI Employees for ${config.industryDisplay} illustration`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.seo.title} />
        <meta name="twitter:description" content={config.seo.description} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generateFAQSchema(faq.map((f) => ({ question: f.q, answer: f.a })))} />
      <JsonLd
        data={generateBreadcrumbSchema([
          { name: 'Home', url: `${SEO_DEFAULTS.url}/` },
          { name: 'AI Employees', url: `${SEO_DEFAULTS.url}/ai-employees` },
          { name: `For ${config.industryDisplay}`, url: canonicalUrl },
        ])}
      />

      <div className={`aie-landing rolp vlp ${dark ? 'dark' : ''}`}>
        {/* Hero */}
        <section className="rolp-hero">
          <div className="rolp-hero__glow" aria-hidden="true" />
          <div className="rolp-hero__inner">
            <nav className="rolp-breadcrumb" aria-label="Breadcrumb">
              <Link to="/ai-employees">AI Employees</Link>
              <span aria-hidden="true">/</span>
              <span>For {config.industryDisplay}</span>
            </nav>
            <span className="rolp-hero__pill">
              <span className="rolp-hero__pill-dot" aria-hidden="true" />
              CellCog AI Employees
            </span>
            <h1 className="rolp-hero__title aie-display">
              {config.heroTitle}
              <br />
              <span className="rolp-hero__title-accent">{config.heroAccent}</span>
            </h1>
            <p className="rolp-hero__sub">{config.heroSub}</p>
            <div className="rolp-hero__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleHire('hero')}>
                Get started <ArrowRightOutlined />
              </button>
              <button type="button" className="aie-btn aie-btn-ghost" onClick={() => handleTalk('hero')}>
                Talk to us
              </button>
            </div>
            {HeroVisual && (
              <div className="vlp-hero-visual">
                <HeroVisual />
              </div>
            )}
          </div>
        </section>

        {/* The problem: access framing (never replacement framing) */}
        <Section label="Why now" title={config.problem.title} narrow>
          <div className="vlp-problem">
            {config.problem.paragraphs.map((p) => (
              <p key={p.slice(0, 32)}>{p}</p>
            ))}
          </div>
        </Section>

        {/* Named starter roles */}
        <Section
          label="Where owners start"
          title={config.rolesTitle}
          sub="Each is a real employee with their own role, inbox, and task list. Hire one, or all three."
        >
          <div className="rolp-cap-grid rolp-stagger">
            {config.roles.map((r, i) => (
              <div className="rolp-cap-card" key={r.title}>
                <span className="rolp-cap-num">{String(i + 1).padStart(2, '0')}</span>
                <h3>{r.title}</h3>
                <p>{r.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Shared traits strip (identical across pages by design) */}
        <Section label="Every CellCog AI Employee" title="More than a tool, a real AI employee">
          <div className="rolp-trait-strip rolp-stagger">
            {SHARED_TRAITS.map((t) => (
              <div className="rolp-trait" key={t.title}>
                <CheckCircleOutlined className="rolp-trait__icon" />
                <span><b>{t.title}</b> {t.body}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Honest limits: the trust beat */}
        <Section label="Honest limits" title={config.honesty.title} narrow>
          <div className="vlp-honesty">
            <p>{config.honesty.body}</p>
          </div>
        </Section>

        {/* How hiring works */}
        <Section
          label="How hiring works"
          title="From job post to first task in three steps"
          sub="Hiring is self-serve and takes under a minute."
          narrow
        >
          <div className="vlp-steps rolp-stagger">
            <div className="rolp-limit vlp-step">
              <span className="vlp-step__num">Step 1</span>
              <b>Describe the job in one sentence</b>
              <p>
                Plain English, like a job post. For example:
                <span className="vlp-step__example">&ldquo;{config.hireExample}&rdquo;</span>
              </p>
            </div>
            <div className="rolp-limit vlp-step">
              <span className="vlp-step__num">Step 2</span>
              <b>Grant access and approvals</b>
              <p>Connect the accounts the job needs and set which actions require your sign-off. You stay in control of anything that goes out.</p>
            </div>
            <div className="rolp-limit vlp-step">
              <span className="vlp-step__num">Step 3</span>
              <b>Watch them start working</b>
              <p>The first work comes back as real deliverables, and your AI employee reports back on what got done and what needs your yes.</p>
            </div>
          </div>
        </Section>

        {/* Pricing: tile treatment (Sep 6 2026 ruling: no cost figure here; the
            mechanic line links to /pricing, where the figures live) */}
        <Section label="Pricing" title="Pay for work, not seats" narrow>
          <div className="vlp-price">
            <div className="rolp-cap-card vlp-price__card">
              <span className="vlp-price__num aie-display">Free to hire</span>
              <span className="vlp-price__unit">You pay only for the work they do.</span>
            </div>
            <p className="rolp-footnote">
              You pay for the work, not the hire; the cost depends purely on how much work you
              assign. Current figures are on the <Link to="/pricing">pricing page</Link>.
            </p>
            <div className="rolp-hero__ctas vlp-price__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleHire('pricing')}>
                Get started <ArrowRightOutlined />
              </button>
              <button type="button" className="aie-btn aie-btn-ghost" onClick={() => handleTalk('pricing')}>
                Talk to us
              </button>
            </div>
            <p className="vlp-support">Try it free, no credit card needed. Signup includes 200 free credits.</p>
          </div>
        </Section>

        {/* FAQ */}
        <Section label="FAQ" title="Frequently asked questions" narrow>
          <div className="rolp-faq">
            {faq.map((f) => (
              <details className="rolp-faq__item" key={f.q}>
                <summary>{f.q}</summary>
                <p>
                  {f.a}
                  {f.link && (
                    <>
                      {' '}
                      <Link className="rolp-faq__link" to={f.link.href}>
                        {f.link.label}
                      </Link>
                    </>
                  )}
                </p>
              </details>
            ))}
          </div>
        </Section>

        {/* Related pages: role cluster + the other vertical + the category page */}
        <Section label="Related" title="Hire by role, or explore further">
          <div className="rolp-rel-grid rolp-stagger">
            {config.relatedRoles.map((s) => (
              ROLE_CONFIGS[s] ? (
                <Link className="rolp-rel-card" key={s} to={`/ai-employees/${s}`}>
                  <span>{ROLE_CONFIGS[s].roleDisplay}</span>
                  <ArrowRightOutlined />
                </Link>
              ) : null
            ))}
            {cross && (
              <Link className="rolp-rel-card" to={cross.path}>
                <span>AI Employees for {cross.industryDisplay}</span>
                <ArrowRightOutlined />
              </Link>
            )}
            <Link className="rolp-rel-card" to="/compare/ai-employee-platforms">
              <span>Best AI employee platforms</span>
              <ArrowRightOutlined />
            </Link>
          </div>
        </Section>

        {/* Further reading: blog cluster links */}
        <Section label="Further reading" title="From the blog">
          <FurtherReading slugs={DEFAULT_ROLE_READING} />
        </Section>

        {/* Close band */}
        <section className="rolp-close">
          <div className="rolp-close__glow" aria-hidden="true" />
          <div className="rolp-close__inner">
            <h2 className="aie-display">{config.ctaBand.title}</h2>
            <p>{config.ctaBand.body}</p>
            <div className="rolp-hero__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleHire('close')}>
                Get started <ArrowRightOutlined />
              </button>
              <button type="button" className="aie-btn aie-btn-ghost" onClick={() => handleTalk('close')}>
                Talk to us
              </button>
            </div>
          </div>
        </section>
      </div>

      <RoleContactModal open={modalOpen} onClose={() => setModalOpen(false)} roleDisplay={config.contactRoleDisplay} dark={dark} />
    </>
  );
};

export default VerticalLandingPage;
